import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

/**
 * Compact variant selector used by the V4 homepage product cards.
 * The existing ProductFormComponent owns submission and cart rendering; this
 * component only synchronizes the selected price and closes after success.
 */
class V4CardQuickDialog extends DialogComponent {
  #abortController = new AbortController();
  #imageRequestId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleVariantChange, { signal: this.#abortController.signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  showDialog() {
    super.showDialog();
    requestAnimationFrame(() => {
      this.querySelector('[data-v4-variant-option]:checked')?.nextElementSibling?.scrollIntoView({ block: 'nearest' });
    });
  }

  #handleVariantChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-v4-variant-option]')) return;

    const option = event.target;
    const price = this.querySelector('[data-v4-quick-price]');
    const selectedLabels = this.querySelectorAll('[data-v4-quick-selected], [data-v4-quick-footer-selected]');
    const image = this.querySelector('[data-v4-quick-image]');
    const variantId = this.querySelector('[ref="variantId"]');
    const button = this.querySelector('[ref="addToCartButton"]');

    if (price && option?.dataset.price) price.textContent = option.dataset.price;
    if (variantId instanceof HTMLInputElement) variantId.value = option.value;
    selectedLabels.forEach((label) => {
      label.textContent = option.dataset.title || '';
    });

    if (image instanceof HTMLImageElement && option.dataset.image) this.#updatePreviewImage(image, option);

    if (button instanceof HTMLButtonElement) button.disabled = option?.dataset.available !== 'true';
  };

  #updatePreviewImage(image, option) {
    const src = option.dataset.image;
    if (!src || image.getAttribute('src') === src) return;

    const requestId = ++this.#imageRequestId;
    const preloader = new Image();

    const applyImage = () => {
      if (requestId !== this.#imageRequestId || !option.checked) return;
      image.src = src;
      image.alt = option.dataset.title || '';
    };

    preloader.addEventListener('load', applyImage, { once: true });
    preloader.addEventListener('error', applyImage, { once: true });
    preloader.src = src;
    if (preloader.complete) applyImage();
  }

  #handleCartUpdate = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[v4-card-quick] Cart update rejected:', error);
      });
  };
}

if (!customElements.get('v4-card-quick-dialog')) {
  customElements.define('v4-card-quick-dialog', V4CardQuickDialog);
}
